import dayjs from 'dayjs';
import { css } from 'linaria';
import React, { FC } from 'react';
import { styled } from 'linaria/react';
import { faStar as solidStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as regularStar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { Group, Member } from 'src/domain/group';
import { PeopleIcon } from 'evergreen-ui';
import { routes } from 'src/routes';
import { MakeGroupFavoriteParams } from 'src/store/groups-collection.store';

const Title = styled.h3`
	font-size: 20px;
	font-weight: 500;
	color: var(--blue600);
`;
const Description = styled.p`
	font-size: 14px;
	margin: 8px 0;
`;
const Middle = styled.div`
	display: flex;
	justify-content: space-between;
	column-gap: 16px;
	margin-top: auto;
	padding: 16px 0;
`;
const Creator = styled.div`
	font-size: 14px;
	color: var(--blue600);
`;
const MembersCount = styled.div`
	display: flex;
	align-items: center;
	font-size: 14px;
	color: var(--blue600);
	
	svg {
		display: flex;
		margin-right: 4px;
	}
`;
const Bottom = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;
const Button = styled.div`
	background: transparent;
	border: transparent;
	cursor: pointer;
`;
const Date = styled.div`
	font-size: 14px;
	color: var(--gray900);
`;
const linkClass = css`
	&[data-expired='true'] {
		cursor: default;
		pointer-events: none;
	}
	&[data-through='true'] {
		text-decoration: line-through;
        color: var(--blue600);
	}
`;
const GroupCardContainer = styled.div`
	display: flex;
	flex-direction: column;
  padding: 16px;
  border-radius: 4px;
  background: white;
  position: relative;
  overflow: hidden;
  
  &[data-expired='true'] {
  	opacity: .7;
  }
  
  &[data-member='true'] {
  	&:before {
  		content: '';
  		display: block;
  		position: absolute;
  		height: 10px;
  		width: 50px;
  		background: var(--blue500);
  		right: -15px;
  		top: 5px;
  		transform: rotate(45deg);
  	}
  }
`;

function getOwnerName(owner: Member) {
	const firstName = owner?.user?.details.first_name;
	const lastName = owner?.user?.details.last_name;

	return [firstName, lastName].join(' ');
}

export const GroupCard: FC<{ group: Group, onFavorite: (payload: MakeGroupFavoriteParams) => void; }> = (props) => {
	const { group, onFavorite } = props;

	const isFavorite = group.favorite !== null;
	const ownerName = getOwnerName(group.owner);
	const description = group.description.length > 64 ?
		group.description.slice(0, 32).trim() + '...' :
		group.description;
	const membersCount = group.members;
	const eventDate = dayjs(group.event_date).format('DD.MM.YYYY');
	const isMember = Boolean(group.joined_user);
	const isExpired = dayjs(group.event_date).isBefore(dayjs());

	return (
		<GroupCardContainer data-member={isMember} data-expired={isExpired && !isMember}>
			<Link
				data-expired={isExpired && !isMember}
				data-through={isExpired}
				className={linkClass}
				to={routes.root.home.group.url(group.id)}
			>
				<Title>{group.caption}</Title>
			</Link>
			<Description title={group.description}>{description}</Description>
			<Middle>
				<Creator>{ownerName}</Creator>
				<MembersCount><PeopleIcon/> {membersCount}</MembersCount>
			</Middle>
			<Bottom>
				<Button onClick={() => onFavorite({ group: group.id, type: isFavorite ? 'remove' : 'add' })}>
					<FontAwesomeIcon size={'lg'} color={'var(--blue500)'} icon={isFavorite ? solidStar : regularStar}/>
				</Button>
				<Date>{eventDate}</Date>
			</Bottom>
		</GroupCardContainer>
	);
};
